/**
 * AllInclusiveFeePanel — the manual, all-inclusive fixed fee on a Basic Services proposal.
 *
 * Off by default and invisible in effect: a proposal with no override row prices exactly as it
 * always has, from Airtable's fee engine. Turning this on records a manual total and back-fills
 * the phases by the firm's own proportions, INCLUDING the three phases a standard proposal bills
 * variably (Permitting, Bidding & Negotiation, Construction Support).
 *
 * Every line is editable after the split — the proportional back-fill is a starting point, not a
 * rule. The one thing the panel will not let through is a set of rows that does not add up to the
 * headline number (the server enforces the same check independently); that figure is the client's
 * contract price and becomes real BQE Core phase amounts at execution.
 *
 * See docs/ALL_INCLUSIVE_FEE.md.
 */
import { useEffect, useRef, useState } from 'react'
import { F } from './CrmModal.jsx'
import { alertDialog, confirmDialog } from '../Dialog.jsx'
import {
  ALL_INCLUSIVE_PHASES, CS_SITE_VISITS, BN_DEFAULT_MEETINGS,
  PR_DEFAULT_HEARINGS, PR_DEFAULT_MEETINGS,
  includedCodesFor, proportionsFor, splitFee, overrideDrift,
  phaseLabel, spreadRemainder, shareOfTotal, standardShares,
  getFeeOverride, saveFeeOverride, clearFeeOverride,
} from '../../services/proposalFeeOverride.js'

const money = n => '$' + Math.round(Number(n) || 0).toLocaleString()
const pct1 = f => (Math.round((Number(f) || 0) * 1000) / 10) + '%'

function parseMoney(s) {
  const n = Number(String(s).replace(/[^0-9.-]/g, ''))
  return isNaN(n) ? 0 : n
}

export default function AllInclusiveFeePanel({ proposalId, proposalRaw, includedPhases, adjustments, onChanged }) {
  const [loading, setLoading] = useState(true)
  const [on, setOn] = useState(false)
  const [total, setTotal] = useState('')
  const [fees, setFees] = useState({})          // { code: dollars }
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [resynced, setResynced] = useState(null)  // copy shown after an automatic re-split
  const [shareEdit, setShareEdit] = useState(null) // { code, text } while a share box is being typed in
  // What the CONTRACT commits to for the two phases a fixed fee has just bought. Unbounded is the
  // right promise while they are hourly and an open-ended liability once they are not.
  const [bnMeetings, setBnMeetings] = useState(BN_DEFAULT_MEETINGS)
  const [csVisits, setCsVisits] = useState(CS_SITE_VISITS)
  // Permitting's own allowance, counted as two separate things. Discretionary Approvals has no
  // allowance of its own and defers its meetings HERE, so an uncapped Permitting left DA uncapped.
  const [prHearings, setPrHearings] = useState(PR_DEFAULT_HEARINGS)
  const [prMeetings, setPrMeetings] = useState(PR_DEFAULT_MEETINGS)

  // The phase set and adjustments come from the editor's LIVE form, not the saved record — the two
  // diverge the moment someone edits the phase list, and a split against the stale set prices
  // phases the client is not buying.
  const codes = includedCodesFor(proposalRaw, includedPhases)
  const proportions = proportionsFor(proposalRaw, codes, adjustments)
  const codeSig = codes.join(',')
  // The model's own weights as shares of THIS fee, so they sit directly beside the real ones.
  const stdShares = standardShares(proportions)
  // Phases the user has typed a number into this session. "Balance remainder" spreads the
  // difference across everything EXCEPT these, so a deliberate figure is never quietly moved.
  const pinnedRef = useRef(new Set())

  // Read inside the load effect, which must not re-run when these change (the separate effect
  // below handles a live change). Refs keep it reading the current values without that dependency.
  const codesRef = useRef(codes); codesRef.current = codes
  const proportionsRef = useRef(proportions); proportionsRef.current = proportions

  useEffect(() => {
    let cancelled = false
    if (!proposalId) { setLoading(false); return }
    getFeeOverride(proposalId).then(ov => {
      if (cancelled) return
      if (ov) {
        setOn(true)
        setTotal(String(ov.totalFee))
        setNote(ov.note || '')
        if (ov.bnMeetings != null) setBnMeetings(ov.bnMeetings)
        if (Array.isArray(ov.csVisits)) setCsVisits(ov.csVisits)
        if (ov.prHearings != null) setPrHearings(ov.prHearings)
        if (ov.prMeetings != null) setPrMeetings(ov.prMeetings)
        // Self-heal a split saved against a different phase list. Hand-edited line amounts are
        // lost, but they were already allocating money to the wrong phases — and nothing is
        // written until Save, so this only ever proposes the repair.
        const d = overrideDrift(ov, codesRef.current)
        if (!d.ok) {
          setFees(splitFee(ov.totalFee, proportionsRef.current))
          setDirty(true)
          setResynced('The phases changed since this fee was set, so it has been re-split across the current ones. Check the lines, then save.')
        } else {
          setFees(ov.phaseFees || {})
        }
      }
      setLoading(false)
    }).catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [proposalId])

  // Keep the split honest when the phase selection changes underneath it.
  //
  // Re-splitting proportionally is the right repair: the alternative (leaving a stale split in
  // place) is what produced a document whose rows read $84,990 under a $100,000 total. It does
  // discard hand-edited line amounts, so it says so rather than doing it silently, and it only
  // fires on a REAL change — not on the first render, and not while the row is still loading.
  const lastSigRef = useRef(null)
  useEffect(() => {
    if (loading || !on) return
    if (lastSigRef.current === null) { lastSigRef.current = codeSig; return }
    if (lastSigRef.current === codeSig) return
    lastSigRef.current = codeSig
    const t = parseMoney(total)
    if (!t) return
    setFees(splitFee(t, proportions))
    setDirty(true)
    setResynced('The phases changed, so the fee was re-split across them. Check the lines, then save.')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSig, loading, on])

  // Count ONLY the phases actually on screen. `fees` can still hold a code that has dropped out of
  // the phase list (it is re-split, not rebuilt, and a stale saved row arrives with whatever it was
  // saved with), and summing those made "Allocated" agree with the total while the visible rows did
  // not — $100,000 allocated over rows totalling $75,010, with the difference hidden in a phase no
  // longer rendered. The number under "Allocated" must be the sum of what the reader can see.
  const allocated = codes.reduce((a, c) => a + (Number(fees[c]) || 0), 0)
  const target = parseMoney(total)
  const remaining = Math.round((target - allocated) * 100) / 100
  const balanced = Math.abs(remaining) < 0.01
  // Column sums for the totals row. Standard is normalized across the included set, so it is 100%
  // by construction; Share is the live one — allocated over the headline fee.
  const stdTotal = codes.reduce((a, c) => a + (stdShares[c] || 0), 0)
  const shareTotal = target > 0 ? allocated / target : 0

  // Presentation conflicts. An all-inclusive fee is ONE price: tiered suite pricing offers three,
  // and "all hourly" says there is no fixed fee at all. Neither is fatal to saving the split, but
  // the document would contradict itself, so say so rather than render something incoherent.
  const conflicts = []
  if (proposalRaw?.['Tiered Pricing'] === 'Yes') conflicts.push('Tiered pricing is on — an all-inclusive fee is a single price, not three packages. Set Tiered pricing to No.')
  if (proposalRaw?.['All Hourly Fees'] === true) conflicts.push('All hourly fees is on, which prints "Hourly" in place of every fee. Turn it off.')

  // The split as it stands versus the phases the proposal actually sells. This is the check that
  // catches a stale saved override — the document refuses to render while these disagree, because
  // a phase sold but not priced falls back to the fee engine's own number and the rows stop adding
  // up to the quoted total.
  const drift = on ? overrideDrift({ phaseFees: fees }, codes) : { ok: true, missing: [], extra: [] }
  if (!drift.ok) {
    if (drift.missing.length) conflicts.push(`Sold but not priced: ${drift.missing.map(phaseLabel).join(', ')}. Re-split so every phase in the proposal carries part of the fee.`)
    if (drift.extra.length) conflicts.push(`Priced but not sold: ${drift.extra.map(phaseLabel).join(', ')}. This money is hidden in a phase the client is not buying. Re-split to move it onto the phases they are.`)
  }

  function resplit(nextTotal) {
    const t = parseMoney(nextTotal)
    setFees(splitFee(t, proportions))
    setDirty(true)
    setResynced(null)
  }

  /**
   * Commit a typed share — sets THAT phase only.
   *
   * It used to rebalance every other phase on each edit, which made setting several in a row
   * self-defeating: pin Permitting at 10%, then set Schematic Design, and Permitting moved off the
   * 10% just chosen. Now a typed share is taken literally and the totals row shows what the set
   * currently adds to, with "Balance remainder" as the explicit step that makes it 100%.
   *
   * The box holds text while being typed (`shareEdit`) so a half-entered "1" on the way to "10"
   * does not momentarily reprice the phase at 1%.
   */
  function commitShare(code) {
    const edit = shareEdit
    setShareEdit(null)
    if (!edit || edit.code !== code) return
    const t = parseMoney(total)
    const n = Number(String(edit.text).replace(/[^0-9.]/g, ''))
    if (!t || isNaN(n)) return
    const next = Math.max(0, Math.min(100, n))
    if (Math.abs(next / 100 - shareOfTotal(fees[code], t)) < 0.0001) return
    pinnedRef.current.add(code)
    setFees(f => ({ ...f, [code]: Math.round((t * next / 100) / 10) * 10 }))
    setDirty(true)
    setResynced(null)
  }

  /** Make the rows add up, without disturbing anything hand-set. */
  function balanceRemainder() {
    const t = parseMoney(total)
    if (!t) return
    const free = codes.filter(c => !pinnedRef.current.has(c))
    setFees(f => spreadRemainder(f, t, free.length ? free : codes))
    setDirty(true)
    setResynced(null)
  }

  function enable() {
    setOn(true)
    setDirty(true)
    if (!total) return
    resplit(total)
  }

  async function disable() {
    if (!(await confirmDialog({
      title: 'Return to the standard fee?',
      message: 'This proposal will price from the fee engine again, and the manual split will be discarded. The engine has been computing underneath the whole time, so nothing needs rebuilding.',
      confirmLabel: 'Use the standard fee',
      danger: true,
    }))) return
    setBusy(true)
    try {
      await clearFeeOverride(proposalId)
      setOn(false); setTotal(''); setFees({}); setNote(''); setDirty(false)
      onChanged && onChanged(null)
    } catch (e) {
      await alertDialog({ title: 'Could not revert', message: e.message })
    } finally { setBusy(false) }
  }

  async function save() {
    if (!drift.ok) {
      await alertDialog({
        title: 'The split does not match the phases',
        message: [
          drift.missing.length ? `Sold but not priced: ${drift.missing.map(phaseLabel).join(', ')}.` : '',
          drift.extra.length ? `Priced but not sold: ${drift.extra.map(phaseLabel).join(', ')}.` : '',
          'Use "Split by the usual percentages" to redistribute the fee across the phases this proposal actually sells.',
        ].filter(Boolean).join(' '),
      })
      return
    }
    if (!balanced) {
      await alertDialog({
        title: 'The phases do not add up',
        message: `The phase fees total ${money(allocated)} against an all-inclusive fee of ${money(target)}. Adjust the lines, or use "Split by the usual percentages" to start over.`,
      })
      return
    }
    setBusy(true)
    try {
      // Write only the phases in scope, for the same reason "Allocated" counts only those: a code
      // left over from an earlier phase list would be saved as real money against a phase the
      // proposal does not sell, and the server's own parts-equal-the-whole check would pass on a
      // set the document never renders.
      const scoped = {}
      for (const c of codes) scoped[c] = Number(fees[c]) || 0
      const ov = await saveFeeOverride(proposalId, {
        totalFee: target, phaseFees: scoped, proportions, note,
        // Only send what this fee actually bought. Sending a visit list for a proposal whose
        // Construction Support is still hourly would freeze a commitment the contract never makes.
        bnMeetings: codes.includes('BN') ? bnMeetings : undefined,
        csVisits: codes.includes('CS') ? csVisits : undefined,
        prHearings: codes.includes('PR') ? prHearings : undefined,
        prMeetings: codes.includes('PR') ? prMeetings : undefined,
      })
      setDirty(false)
      onChanged && onChanged(ov)
    } catch (e) {
      await alertDialog({ title: 'Could not save', message: e.message })
    } finally { setBusy(false) }
  }

  if (!proposalId) {
    return (
      <div style={S.wrap}>
        <div style={S.head}>All-inclusive fixed fee</div>
        <div style={S.muted}>Create the proposal first, then set an all-inclusive fee here.</div>
      </div>
    )
  }
  if (loading) return <div style={S.wrap}><div style={S.head}>All-inclusive fixed fee</div><div style={S.muted}>Loading…</div></div>

  if (!on) {
    return (
      <div style={S.wrap}>
        <div style={S.head}>All-inclusive fixed fee</div>
        <div style={S.muted}>
          This proposal prices from the fee engine, with Permitting, Bidding &amp; Negotiation and
          Construction Support billed variably. Turn this on only when the client has been quoted
          one all-inclusive number covering those too.
        </div>
        <button style={{ ...F.btnGhost, marginTop: 10 }} onClick={enable}>Set an all-inclusive fee…</button>
      </div>
    )
  }

  return (
    <div style={{ ...S.wrap, ...S.wrapOn }}>
      <div style={S.headRow}>
        <div style={{ ...S.head, marginBottom: 0 }}>All-inclusive fixed fee</div>
        <button style={S.linkBtn} onClick={disable} disabled={busy}>Use the standard fee instead</button>
      </div>

      {conflicts.map(c => <div key={c} style={S.warn}>{c}</div>)}
      {resynced && <div style={S.info}>{resynced}</div>}

      <div style={S.totalRow}>
        <div style={{ flex: '0 0 200px' }}>
          <label style={F.label}>All-inclusive fee</label>
          <input
            style={{ ...F.input, fontFamily: '"DM Mono", monospace' }}
            value={total}
            placeholder="100,000"
            onChange={e => { setTotal(e.target.value); setDirty(true) }}
            onBlur={e => { if (!allocated) resplit(e.target.value) }}
          />
        </div>
        <button style={{ ...F.btnGhost, alignSelf: 'flex-end' }} onClick={() => resplit(total)} disabled={busy || !parseMoney(total)}>
          Split by the usual percentages
        </button>
        <div style={{ alignSelf: 'flex-end', marginLeft: 'auto', textAlign: 'right' }}>
          <div style={S.allocLabel}>Allocated</div>
          <div style={{ ...S.allocVal, color: balanced ? 'var(--aj-text)' : '#c0392b' }}>{money(allocated)}</div>
          {!balanced && <div style={S.allocDiff}>{remaining > 0 ? `${money(remaining)} left to allocate` : `${money(-remaining)} over`}</div>}
        </div>
      </div>

      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, textAlign: 'left' }}>Phase</th>
            <th style={S.th} title="What the firm's own fee model would give this phase, as a share of this fee">Standard</th>
            <th style={S.th} title="This phase's share of the all-inclusive fee — edit it and the others absorb the difference">Share</th>
            <th style={S.th}>Fee</th>
          </tr>
        </thead>
        <tbody>
          {ALL_INCLUSIVE_PHASES.filter(p => codes.includes(p.code)).map(p => {
            const std = stdShares[p.code] || 0
            const share = shareOfTotal(fees[p.code], target)
            // Flag a line the firm has moved meaningfully off its own model, in either direction.
            const moved = target > 0 && Math.abs(share - std) >= 0.01
            return (
              <tr key={p.code}>
                <td style={S.td}>
                  {p.label}
                  {p.normallyVariable && <span style={S.tag} title="Normally billed hourly — this fee absorbs it">normally hourly</span>}
                </td>
                <td style={{ ...S.td, ...S.tdNum, color: 'var(--aj-text-muted)' }}>{std ? pct1(std) : '—'}</td>
                <td style={{ ...S.td, ...S.tdNum }}>
                  <input
                    style={{ ...S.pctInput, ...(moved ? S.pctMoved : null) }}
                    value={shareEdit && shareEdit.code === p.code ? shareEdit.text : (target > 0 ? pct1(share).replace('%', '') : '')}
                    placeholder="0"
                    title={moved ? `Standard would be ${pct1(std)}` : undefined}
                    onChange={e => setShareEdit({ code: p.code, text: e.target.value })}
                    onBlur={() => commitShare(p.code)}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                  />
                  <span style={S.pctSign}>%</span>
                </td>
                <td style={{ ...S.td, ...S.tdNum }}>
                  <input
                    style={S.feeInput}
                    value={fees[p.code] != null ? String(fees[p.code]) : ''}
                    placeholder="0"
                    onChange={e => { setFees(f => ({ ...f, [p.code]: parseMoney(e.target.value) })); setDirty(true) }}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
        {/* The summation. The whole point of an all-inclusive fee is that the parts equal the
            whole, so the check belongs at the foot of the rows being edited — not only in the
            "Allocated" figure at the top, which is out of eyeline while typing percentages. */}
        <tfoot>
          <tr>
            <td style={S.tdTotal}>Total</td>
            <td style={{ ...S.tdTotal, ...S.tdNum, color: 'var(--aj-text-muted)' }}>{pct1(stdTotal)}</td>
            <td style={{ ...S.tdTotal, ...S.tdNum, color: balanced ? '#1f9d57' : '#c0392b' }}>
              {pct1(shareTotal)}
            </td>
            <td style={{ ...S.tdTotal, ...S.tdNum, color: balanced ? 'var(--aj-text)' : '#c0392b' }}>
              {money(allocated)}
            </td>
          </tr>
          {!balanced && (
            <tr>
              <td style={S.tdNote} colSpan={2}>
                <button style={S.balanceBtn} onClick={balanceRemainder}>Balance remainder</button>
                <span style={S.balanceHint}>
                  {pinnedRef.current.size
                    ? ` spreads it across the phases you haven't set`
                    : ` spreads it across every phase`}
                </span>
              </td>
              <td style={{ ...S.tdNote, ...S.tdNum, color: '#c0392b' }}>
                {remaining > 0 ? '+' : '−'}{pct1(Math.abs(remaining) / (target || 1))}
              </td>
              <td style={{ ...S.tdNote, ...S.tdNum, color: '#c0392b' }}>
                {remaining > 0 ? `${money(remaining)} short` : `${money(-remaining)} over`}
              </td>
            </tr>
          )}
        </tfoot>
      </table>

      {/* What the CONTRACT commits to for the two phases the fee just bought. These print in
          Exhibit C and are frozen onto the proposal, so they never change under a signed client. */}
      {(codes.includes('CS') || codes.includes('BN') || codes.includes('PR')) && (
        <div style={S.commitBox}>
          <div style={S.commitHead}>Included in the fixed fee — printed in the contract</div>

          {codes.includes('BN') && (
            <div style={S.commitRow}>
              <label style={F.label}>Bidding &amp; Negotiation meetings</label>
              <input
                style={{ ...S.pctInput, width: 60 }}
                value={String(bnMeetings)}
                onChange={e => {
                  const n = Number(String(e.target.value).replace(/[^0-9]/g, ''))
                  setBnMeetings(isNaN(n) ? 0 : Math.min(20, n)); setDirty(true)
                }}
              />
              <span style={S.commitHint}>
                Typically two: the contractor walk-through and the bid review. Beyond this, hourly.
              </span>
            </div>
          )}

          {codes.includes('PR') && (
            <div style={{ marginTop: codes.includes('BN') ? 12 : 0 }}>
              <div style={S.commitRow}>
                <label style={F.label}>Permitting &mdash; public hearings</label>
                <input
                  style={{ ...S.pctInput, width: 60 }}
                  value={String(prHearings)}
                  onChange={e => {
                    const n = Number(String(e.target.value).replace(/[^0-9]/g, ''))
                    setPrHearings(isNaN(n) ? 0 : Math.min(20, n)); setDirty(true)
                  }}
                />
                <label style={{ ...F.label, marginLeft: 12 }}>Client, agency &amp; community meetings</label>
                <input
                  style={{ ...S.pctInput, width: 60 }}
                  value={String(prMeetings)}
                  onChange={e => {
                    const n = Number(String(e.target.value).replace(/[^0-9]/g, ''))
                    setPrMeetings(isNaN(n) ? 0 : Math.min(20, n)); setDirty(true)
                  }}
                />
              </div>
              <div style={S.commitHint}>
                Counted separately &mdash; a hearing is not a staff call. Discretionary Approvals has
                no allowance of its own and draws on this one, so these are the limits for both
                phases. Beyond them, hourly.
              </div>
            </div>
          )}

          {codes.includes('CS') && (
            <div style={{ marginTop: (codes.includes('BN') || codes.includes('PR')) ? 12 : 0 }}>
              <label style={F.label}>
                Construction Support site visits — {csVisits.length} of {CS_SITE_VISITS.length} included
              </label>
              <div style={S.commitHint}>
                These are the phase&apos;s own scheduled milestones. Every one you tick is named in the
                contract as a service provided; visits beyond them are hourly.
              </div>
              <div style={S.visitList}>
                {CS_SITE_VISITS.map(v => {
                  const checked = csVisits.includes(v)
                  return (
                    <label key={v} style={{ ...S.visitRow, opacity: checked ? 1 : 0.55 }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          // Rebuilt from the canonical order, never from tick order, so the contract
                          // always reads in construction sequence.
                          setCsVisits(CS_SITE_VISITS.filter(x => (x === v ? !checked : csVisits.includes(x))))
                          setDirty(true)
                        }}
                      />
                      <span>{v}</span>
                    </label>
                  )
                })}
              </div>
              {!csVisits.length && (
                <div style={S.warn}>
                  No site visits are included. The contract will say so — every visit becomes hourly.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <label style={F.label}>Why this project is all-inclusive (internal)</label>
        <input style={F.input} value={note} placeholder="e.g. client asked for one number covering permitting and construction support"
          onChange={e => { setNote(e.target.value); setDirty(true) }} />
      </div>

      <div style={S.footNote}>
        Reimbursable expenses, long-distance travel, and meetings or site visits beyond those set
        above stay variable and still print on the proposal.
      </div>

      {/* An unsaved edit here is not a small thing: Preview PDF sits in the editor's own footer and
          renders from the SAVED row, so previewing now shows the previous numbers with nothing to
          say so. This used to be a grey aside beside the button and was missed in practice. */}
      {dirty && (
        <div style={S.dirtyBanner}>
          <strong>Not saved.</strong> Preview PDF and the client&apos;s document still show the
          previously saved amounts, not what is on screen. Save first.
        </div>
      )}

      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button style={F.btnPrimary} onClick={save} disabled={busy || !balanced || !drift.ok}>
          {busy ? 'Saving…' : dirty ? 'Save all-inclusive fee' : 'Saved'}
        </button>
      </div>
    </div>
  )
}

const S = {
  wrap: { border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, background: 'var(--aj-bg-subtle)' },
  wrapOn: { borderColor: 'rgba(29,158,117,0.45)', background: 'rgba(29,158,117,0.05)' },
  head: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 },
  headRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.5 },
  linkBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0 },
  info: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#1f6f8a', background: 'rgba(31,111,138,0.10)', border: '0.5px solid rgba(31,111,138,0.32)', borderRadius: 4, padding: '6px 9px', marginBottom: 8 },
  warn: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#8a4b00', background: 'rgba(230,145,0,0.10)', border: '0.5px solid rgba(230,145,0,0.35)', borderRadius: 4, padding: '6px 9px', marginBottom: 8 },
  totalRow: { display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 },
  allocLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', letterSpacing: '0.04em' },
  allocVal: { fontFamily: '"DM Mono", monospace', fontSize: 15 },
  allocDiff: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#c0392b' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 600, color: 'var(--aj-text-muted)', textAlign: 'right', padding: '0 0 4px', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--aj-border)' },
  td: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', padding: '5px 0', borderBottom: '0.5px solid var(--aj-border)' },
  tdTotal: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', padding: '7px 0 2px', borderTop: '1px solid var(--aj-border-mid)' },
  tdNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', padding: '1px 0 4px' },
  balanceBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, color: '#07272D', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' },
  balanceHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' },
  tdNum: { textAlign: 'right', fontFamily: '"DM Mono", monospace' },
  pctInput: { width: 52, textAlign: 'right', fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text)', padding: '4px 5px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)' },
  pctMoved: { borderColor: 'rgba(230,145,0,0.7)', color: '#8a4b00' },
  pctSign: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginLeft: 3 },
  feeInput: { width: 110, textAlign: 'right', fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text)', padding: '4px 7px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)' },
  tag: { marginLeft: 7, fontFamily: '"DM Sans", sans-serif', fontSize: 9.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#1f9d57', border: '0.5px solid rgba(29,158,117,0.4)', borderRadius: 20, padding: '1px 6px' },
  commitBox: { marginTop: 14, paddingTop: 12, borderTop: '0.5px solid var(--aj-border)' },
  commitHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 },
  commitRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  commitHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.45, marginTop: 2 },
  visitList: { marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 14px' },
  visitRow: { display: 'flex', alignItems: 'center', gap: 6, fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer' },
  footNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 10, lineHeight: 1.5 },
  dirtyBanner: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.45, color: '#8a4b00', background: 'rgba(230,145,0,0.14)', border: '0.5px solid rgba(230,145,0,0.5)', borderRadius: 4, padding: '7px 10px', marginTop: 12 },
  dirtyNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#8a4b00' },
}
